import { useWatch, type Control } from 'react-hook-form'

import type { Branch } from '@/api/types'
import { SelectField, TextField, type SelectOption } from '@/components/form/fields'
import { branchDescription } from '@/lib/branch'

import { GENDER_OPTIONS, MARITAL_STATUS_OPTIONS } from '../constants'
import type { DuplicateCheck } from '../use-duplicate-check'
import { formatGhanaCardInput, isGhanaCardShape, latestPermittedBirthDate } from '../validation'
import type { CustomerWizardInput } from '../validation'
import { DuplicateNotice } from './duplicate-notice'
import { FormattedTextField } from './formatted-field'
import { StepSection } from './step-section'

export interface IdentityStepProps {
  control: Control<CustomerWizardInput>
  branches: Branch[]
  duplicate: DuplicateCheck
}

export function IdentityStep({ control, branches, duplicate }: IdentityStepProps) {
  const ghanaCardNumber = useWatch({ control, name: 'ghanaCardNumber' })

  // The branch decides which staff can see this file at all, so it is captured deliberately even
  // where the user only has one — a single-branch list still shows which one it is.
  // The name is null until an organisation module owns branches as records, so the identifier
  // stands in rather than a fabricated label. Showing "Branch 1" would be a name the institution
  // never chose, and it would keep looking correct after the real names arrive.
  const branchOptions: SelectOption[] = branches.map((branch) => ({
    value: branch.id,
    label: branchDescription(branch),
  }))

  return (
    <div className="space-y-6">
      <StepSection
        title="Name"
        description="Spelled exactly as it appears on the Ghana Card, including the order of the names."
      >
        <TextField control={control} name="firstName" label="First name" required autoComplete="off" />
        <TextField control={control} name="middleName" label="Middle name" autoComplete="off" />
        <TextField control={control} name="lastName" label="Surname" required autoComplete="off" />
        <SelectField
          control={control}
          name="branchId"
          label="Home branch"
          options={branchOptions}
          placeholder="Select a branch…"
          required
          description="Identity is global; the branch decides who can open this file."
        />
      </StepSection>

      <StepSection title="Birth and status">
        <SelectField
          control={control}
          name="gender"
          label="Gender"
          options={GENDER_OPTIONS}
          placeholder="Select…"
          required
        />
        <TextField
          control={control}
          name="dateOfBirth"
          label="Date of birth"
          type="date"
          required
          // The upper bound is the minimum-age rule expressed where a date picker can enforce it.
          // The server refuses an underage record regardless.
          max={latestPermittedBirthDate()}
          description="At least 18 years old."
        />
        <TextField control={control} name="placeOfBirth" label="Place of birth" required />
        <TextField
          control={control}
          name="nationality"
          label="Nationality"
          required
          description="Two-letter country code. GH for a Ghanaian national."
        />
        <SelectField
          control={control}
          name="maritalStatus"
          label="Marital status"
          options={MARITAL_STATUS_OPTIONS}
          placeholder="Select…"
          required
        />
      </StepSection>

      <StepSection
        title="Ghana Card"
        description="The identity anchor. It is unique across the whole institution, and no other document replaces it for a Ghanaian resident."
      >
        <FormattedTextField
          control={control}
          name="ghanaCardNumber"
          label="Ghana Card number"
          required
          inputMode="numeric"
          placeholder="GHA-123456789-0"
          description="Type the digits; the prefix and dashes are added for you."
          format={formatGhanaCardInput}
          onValueBlur={(value) => {
            // A half-typed number is not a question worth asking the server.
            if (isGhanaCardShape(value)) {
              duplicate.check(value)
            }
          }}
          className="sm:col-span-2"
        >
          <DuplicateNotice
            field="ghanaCardNumber"
            status={duplicate.statusFor(ghanaCardNumber)}
            existingCustomerId={duplicate.existingCustomerId}
          />
        </FormattedTextField>

        <TextField
          control={control}
          name="ghanaCardIssueDate"
          label="Issued"
          type="date"
          description="Optional."
        />
        <TextField
          control={control}
          name="ghanaCardExpiryDate"
          label="Expires"
          type="date"
          description="An expired card raises a KYC review task."
        />
      </StepSection>
    </div>
  )
}
